import type { AttributeCheckResult, PendingReadingChecks, ReadingCheck } from '../../api/client';
import { Alert } from '../../components/ui';

// How Pending Approvals words the reading checks billing-api runs before a
// manual reading is written (billing-core readingChecks.ts). The server holds
// the row; these only explain it. No em-dashes: this is copy a person reads
// while deciding whether to overwrite a meter's history.

const UNITS: Record<string, string> = {
  waterLevel: 'L',
  Import_kWh: 'kWh',
  Export_kWh: 'kWh',
};

/** A quantity with its unit, joined by U+00A0 so the unit never wraps away. */
function qty(value: number, attribute: string): string {
  const unit = UNITS[attribute];
  const n = value.toLocaleString('en-ZA', { maximumFractionDigits: 3 });
  return unit ? `${n} ${unit}` : n;
}

const SAST = new Intl.DateTimeFormat('en-ZA', {
  timeZone: 'Africa/Johannesburg',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** A moment in South African time, e.g. "05 Aug 2026, 08:31". */
export function formatSast(ms: number): string {
  return SAST.format(ms);
}

export function describeReadingCheck(check: ReadingCheck, attribute: string): string {
  switch (check.kind) {
    case 'would_overwrite':
      return `Would replace the stored ${qty(check.existing_value, attribute)} with ${qty(check.new_value, attribute)}.` +
        (check.existing_source ? ` The stored value came from ${check.existing_source}.` : '');
    case 'below_previous':
      return `Lower than the previous reading, ${qty(check.previous_value, attribute)} on ${formatSast(check.previous_at)}.`;
    case 'above_next':
      return `Higher than the next reading, ${qty(check.next_value, attribute)} on ${formatSast(check.next_at)}.`;
    case 'unusual_usage':
      return `Uses ${check.ratio.toLocaleString('en-ZA', { maximumFractionDigits: 1 })} times the meter's usual daily use ` +
        `(${qty(check.implied_daily, attribute)} a day, usually ${qty(check.typical_daily, attribute)}).`;
  }
}

function heldAttributes(result: PendingReadingChecks | undefined): AttributeCheckResult[] {
  if (!result || result.status !== 'checked') return [];
  return result.attributes.filter((a) => !a.already_recorded && a.checks.length > 0);
}

/** The row is held: approving needs an explicit confirmation. */
export function needsConfirmation(result: PendingReadingChecks | undefined): boolean {
  return heldAttributes(result).length > 0;
}

/** "Confirm overwrite" when the reading would replace a stored value, else "Approve anyway". */
export function confirmLabel(result: PendingReadingChecks | undefined): 'Confirm overwrite' | 'Approve anyway' {
  return heldAttributes(result).some((a) => a.checks.some((c) => c.kind === 'would_overwrite'))
    ? 'Confirm overwrite'
    : 'Approve anyway';
}

export function ReadingChecksNotice({ result }: { result: PendingReadingChecks | undefined }) {
  if (!result || result.status !== 'checked') return null;
  if (result.already_recorded) {
    return (
      <Alert tone="info" className="mt-2.5">
        <span className="font-medium">Already recorded:</span> this value is already stored for that time, so approving writes nothing.
      </Alert>
    );
  }
  const held = heldAttributes(result);
  if (held.length === 0) return null;
  const many = held.length > 1;
  return (
    <Alert tone="warning" title="Held for review" className="mt-2.5">
      <ul className="list-disc pl-5 space-y-1">
        {held.flatMap((a) => a.checks.map((c, i) => (
          <li key={`${a.attribute}-${i}`}>
            {many && <span className="font-mono">{a.attribute}: </span>}
            {describeReadingCheck(c, a.attribute)}
          </li>
        )))}
      </ul>
    </Alert>
  );
}
